/**
 * Your biggest move (TIMELINE "The lever sweep"): what-ifs built from the map itself, each run
 * through the engine and ranked by how far it moves one goal. Nothing here is advice and nothing
 * names a product: every move changes an item already on the map, and says what it costs.
 */
import { contract, core, debt as debtNs, formatDay, parseDay, plan as planNs, schedule, type Day, type GoalProgress, type Plan, type Transaction } from '@map-my-money/engine';
// The one change-set applier, shared with Claude's run_what_if and the web's What If; it reaches only engine and views.
import { applyChangeSet, type PlanChangeSet } from '../../cloud/src/mcp/plan-items.ts';
import { calendarMonths, compareForecasts } from './compare.ts';
import { valuesOn } from './dated.ts';
import { itemEnd } from './ends.ts';
import { money, type Region } from './money.ts';
import { largestIncome } from './plan-editor.ts';
import { decode, planAsOf, runForecast, type Forecasted, type WaypointInput, type WirePlan } from './run.ts';
import { readableMonth } from './stamp.ts';

export type MoveKind = 'cut' | 'redirect' | 'pension' | 'overpay' | 'date';

export interface MoveItem {
  readonly id: string;
  readonly label: string;
}

/** Where a move's money goes: a pot, a debt, or simply stays in cash. */
export interface MoveDestination extends MoveItem {
  readonly kind: 'investment' | 'debt' | 'cash';
}

export interface MoveEffect {
  readonly goal_id: string;
  /** The goal's date before and after (YYYY-MM-DD month ends; today once reached), null when not within the horizon. */
  readonly before: string | null;
  readonly after: string | null;
  readonly months_sooner: number | null;
  /**
   * Days sooner: from the payoff days for a debt goal, else where the line crosses the target, read
   * straight between the month ends around it (an estimate finer than months). Null unless both reach it.
   */
  readonly days_sooner: number | null;
  readonly becomes_reachable: boolean;
  /** How much further toward its target the goal is with the move on `measured_on` (compare's `gain`): the shortfall it closes by a "by" day. */
  readonly gain: string;
  readonly measured_on: string;
  /** With a "by" day: the gap to the target at the end of the by month, without and with the move. */
  readonly shortfall_before?: string;
  readonly shortfall_after?: string;
}

export interface Move {
  /** Stable for the same map: `cut:eating-out>isa`, `redirect:car-loan>isa`, `pension:workplace`, `overpay:card`, `date:deposit`. */
  readonly id: string;
  readonly kind: MoveKind;
  /** The item the move changes: the spending line cut, the debt that ends, the pension, the debt overpaid, the goal. */
  readonly item: MoveItem;
  readonly to: MoveDestination | null;
  /** What it costs, per payment of `frequency`, as a positive amount: the cut, the payment kept going, the extra paid in. "0" for a date. */
  readonly amount: string;
  readonly frequency: 'weekly' | 'fortnightly' | 'monthly' | 'quarterly' | 'yearly';
  /** `amount` as a monthly figure. */
  readonly monthly: string;
  /** The first day it counts: today, or for a redirect the day after the debt's last payment. */
  readonly from: string;
  /** For a pension move, the extra as a percent of pay. */
  readonly percent_of_pay?: number;
  /** For a debt goal: the debt this one's payment, the extra included, rolls on to once it is cleared. */
  readonly then?: MoveDestination;
  /** For a date move, the new "by" day. */
  readonly by?: string;
  /** The change set (Claude's run_what_if and the web's What If take it as it is). */
  readonly changes: PlanChangeSet;
  readonly effect: MoveEffect;
}

export interface BiggestMoves {
  /** At most `limit` moves, biggest first, each moving the goal meaningfully; then, for a goal behind its by day, the date it would need. */
  readonly moves: readonly Move[];
  /** The month end every move's gain is measured on: the goal's by month, else the month it is due. */
  readonly measuredOn: string | null;
  /** The goal measured: the one asked for, else the first by priority still to reach. */
  readonly goalId: string | null;
}

export interface MoveOptions {
  readonly goalId?: string;
  /** Moves to return, the date move aside (default 3). */
  readonly limit?: number;
}

/** A spending line is cut by this share. */
export const CUT_PERCENT = 10;
/** A pension move pays this much more of the linked pay. */
export const PENSION_PERCENT = 1;
/** Moves are measured on a line this long at most; goals further away get no moves. */
export const MOVES_MAX_YEARS = 30;

/**
 * Spending a cut would read as advice to move house, change jobs or skip tax: housing, tax,
 * insurance, childcare and repayments. Matched on the item's label, in English.
 */
const COMMITMENTS = /\b(rent|mortgage|council tax|tax|taxes|insurance|escrow|loan|loans|repayment|nursery|daycare|childcare|child care|after-school|school|tuition|alimony|maintenance)\b/i;

const PENSIONS = new Set(['workplace_pension', 'sipp', '401k_traditional', '401k_roth']);

const FREQUENCIES = ['weekly', 'fortnightly', 'monthly', 'quarterly', 'yearly'] as const;

type WireTx = NonNullable<WirePlan['transactions']>[number];

const text = (n: number): string => String(Math.round(n * 100) / 100);
const occurrences = (t: WireTx): number => schedule.annualOccurrences(contract.decodeFrequency(t.frequency));
const frequencyOf = (t: WireTx): Move['frequency'] | null => {
  const f = t.frequency ?? 'monthly';
  return typeof f === 'string' && (FREQUENCIES as readonly string[]).includes(f) ? (f as Move['frequency']) : null;
};
const amountOn = (t: WireTx, day: string): number => Number(valuesOn(t, day).amount ?? t.amount);

/** Regular, running on `day`, and with no change planned after it, so a change from `day` means what it says. */
function steady(t: WireTx, day: string): boolean {
  return t.isActive !== false && frequencyOf(t) !== null && (t.endDate === undefined || t.endDate === null || t.endDate >= day)
    && !(t.history ?? []).some((c) => c.from > day);
}

const isPension = (t: WireTx): boolean => t.type === 'investment' && t.taxRelief !== undefined && PENSIONS.has(t.taxRelief.accountType);

/** A pot paying in on `day`: never a withdrawal (a negative amount draws the pot down). */
const payingIn = (t: WireTx, day: string): boolean => t.type === 'investment' && steady(t, day) && amountOn(t, day) >= 0;

interface Context {
  readonly wire: WirePlan;
  readonly asOf: string;
  readonly today: Day;
  readonly plan: Plan;
  /** The plan's debts as the check-ins leave them (typed balances re-anchored). */
  readonly debts: ReadonlyMap<string, Transaction>;
  readonly goal: GoalProgress;
  /** For a debt goal, see `debtOrder`. */
  readonly decider: WireTx | null;
  readonly runnerUp: WireTx | null;
  readonly transactions: readonly WireTx[];
  readonly limit: number;
  /** The last day of the line moves are measured on: a redirect starting after it cannot move the goal. */
  readonly horizon: string;
}

const label = (t: WireTx): string => t.label || t.id;

/** Debts still owing on `day` that take a regular payment, highest rate first; a debt goal counts only those on the debt line. */
function owingDebts(ctx: Context, day: string, debtLineOnly: boolean): WireTx[] {
  const properties = planNs.propertyIds(ctx.plan);
  return ctx.transactions
    .filter((t) => {
      const d = ctx.debts.get(t.id);
      if (t.type !== 'debt' || !d || !steady(t, ctx.asOf) || t.debtIsInterestOnly === true || amountOn(t, ctx.asOf) <= 0) return false;
      if (debtLineOnly && d.linkedAssetId !== null && properties.has(d.linkedAssetId)) return false;
      return core.gt(core.abs(debtNs.remainingBalance(d, parseDay(day), ctx.today)), core.ZERO);
    })
    .sort((a, b) => Number(b.interestRate ?? 0) - Number(a.interestRate ?? 0));
}

/**
 * For a debt goal: the debt that clears last (it decides when the person is debt free) and the one
 * clearing just before it. Extra money goes to the first; when it clears, its payment rolls on to the
 * second, so money sent to debts keeps working until the last one is gone.
 */
function debtOrder(ctx: Context): { decider: WireTx | null; runnerUp: WireTx | null } {
  if (ctx.goal.goal.series !== 'debt') return { decider: null, runnerUp: null };
  const payoff = (t: WireTx) => debtNs.payoffDate(ctx.debts.get(t.id)!, ctx.today, ctx.today) ?? Number.POSITIVE_INFINITY;
  const byPayoff = owingDebts(ctx, ctx.asOf, true).sort((a, b) => payoff(b) - payoff(a) || Number(b.interestRate ?? 0) - Number(a.interestRate ?? 0));
  return { decider: byPayoff[0] ?? null, runnerUp: byPayoff[1] ?? null };
}

const asDebt = (t: WireTx): MoveDestination => ({ id: t.id, label: label(t), kind: 'debt' });

/**
 * Where money freed for the goal goes: for a debt goal the debt that clears last, for net worth a pot
 * you can reach before retiring (else any pot, else cash), for an investment goal the first pot, for
 * a cash goal cash.
 */
function destination(ctx: Context): MoveDestination | null {
  const series = ctx.goal.goal.series;
  if (series === 'debt') return ctx.decider ? asDebt(ctx.decider) : null;
  if (series === 'investment' || series === 'netWorth') {
    const pots = ctx.transactions.filter((t) => payingIn(t, ctx.asOf));
    const pot = series === 'netWorth' ? (pots.find((t) => !isPension(t)) ?? pots[0]) : pots[0];
    if (pot) return { id: pot.id, label: label(pot), kind: 'investment' };
    return series === 'netWorth' ? { id: 'cash', label: 'Cash', kind: 'cash' } : null;
  }
  return series === 'cash' ? { id: 'cash', label: 'Cash', kind: 'cash' } : null;
}

type Update = NonNullable<PlanChangeSet['update']>[number];
type Redirect = NonNullable<PlanChangeSet['redirect']>[number];

/** The step that pays `perMonth` more into `to` from `from`, in `to`'s own frequency; none when it stays in cash. */
function into(ctx: Context, to: MoveDestination, perMonth: number, from: string): Update[] {
  if (to.kind === 'cash') return [];
  const target = ctx.transactions.find((t) => t.id === to.id)!;
  const extra = (perMonth * 12) / occurrences(target);
  return [{ id: to.id, kind: 'transaction', set: { amount: text(amountOn(target, from) + extra) }, from }];
}

/** The debt goal's roll-on: once the debt that clears last is paid off, its payment goes to the runner-up (a no-op unless a move clears it first). */
function rollOn(ctx: Context, except?: string): { then?: MoveDestination; redirect?: Redirect[] } {
  if (!ctx.decider || !ctx.runnerUp || ctx.runnerUp.id === except) return {};
  return { then: asDebt(ctx.runnerUp), redirect: [{ from: ctx.decider.id, to: ctx.runnerUp.id, when: 'ends' }] };
}

interface Candidate extends Omit<Move, 'effect'> {}

/** A roll-on that never pays (the runner-up is cleared before the debt rolling into it) is left out of the move and its words. */
function withoutIdleRoll(c: Candidate, changed: WirePlan, ctx: Context): Candidate {
  if (!c.then) return c;
  const from = c.kind === 'overpay' && c.then.id === ctx.decider?.id ? c.item.id : ctx.decider!.id;
  const source = itemEnd(changed, from, ctx.asOf);
  const target = itemEnd(changed, c.then.id, ctx.asOf);
  if (source === null || target === null || target.last >= source.next) return c;
  const redirect = (c.changes.redirect ?? []).filter((r) => !(r.from === from && r.to === c.then!.id));
  const { then: _, ...rest } = c;
  const { redirect: __, ...changes } = c.changes;
  return { ...rest, changes: { ...changes, ...(redirect.length ? { redirect } : {}) } };
}

function candidates(ctx: Context): Candidate[] {
  const { asOf } = ctx;
  const out: Candidate[] = [];
  const base = (t: WireTx, amount: number, monthly: number) => ({ amount: text(amount), frequency: frequencyOf(t)!, monthly: text(monthly) });
  const steps = (update: Update[], roll: ReturnType<typeof rollOn>, redirect: Redirect[] = []): PlanChangeSet => {
    const redirects = [...redirect, ...(roll.redirect ?? [])];
    return { ...(update.length ? { update } : {}), ...(redirects.length ? { redirect: redirects } : {}) };
  };

  const to = destination(ctx);
  const cuts: Candidate[] = [];
  for (const t of ctx.transactions) {
    if (to === null || t.type !== 'expense' || !steady(t, asOf) || COMMITMENTS.test(label(t)) || t.linkedAssetId !== undefined) continue;
    const now = amountOn(t, asOf);
    const cut = now - Math.round(now * (1 - CUT_PERCENT / 100) * 100) / 100;
    const monthly = (cut * occurrences(t)) / 12;
    if (monthly < 1) continue;
    const roll = rollOn(ctx);
    cuts.push({
      id: `cut:${t.id}>${to.id}`, kind: 'cut', item: { id: t.id, label: label(t) }, to, ...(roll.then ? { then: roll.then } : {}), ...base(t, cut, monthly), from: asOf,
      changes: steps([{ id: t.id, kind: 'transaction', by_percent: -CUT_PERCENT, from: asOf }, ...into(ctx, to, monthly, asOf)], roll),
    });
  }
  // Cuts into one place from one day move the goal in step with their size, so only the largest can rank.
  out.push(...cuts.sort((a, b) => Number(b.monthly) - Number(a.monthly) || (a.id < b.id ? -1 : 1)).slice(0, ctx.limit + 2));

  for (const t of ctx.transactions) {
    if (to === null || to.kind === 'cash' || t.type !== 'debt' || !steady(t, asOf) || t.id === to.id) continue;
    const end = itemEnd(ctx.wire, t.id, asOf);
    if (end === null || end.last < asOf || end.next > ctx.horizon) continue;
    const payment = Number(valuesOn(t, end.last).amount ?? t.amount);
    const roll = rollOn(ctx, t.id);
    out.push({
      id: `redirect:${t.id}>${to.id}`, kind: 'redirect', item: { id: t.id, label: label(t) }, to, ...(roll.then ? { then: roll.then } : {}), ...base(t, payment, payment), from: end.next,
      changes: steps([], roll, [{ from: t.id, to: to.id, when: 'ends' }]),
    });
  }

  const income = largestIncome(ctx.wire, ctx.today);
  for (const t of ctx.transactions) {
    if (!isPension(t) || !payingIn(t, asOf)) continue;
    const pay = ctx.transactions.find((x) => x.id === (t.employerMatchIncomeId ?? income?.id) && x.type === 'income');
    if (!pay || !steady(pay, asOf)) continue;
    const extra = (amountOn(pay, asOf) * occurrences(pay) * PENSION_PERCENT) / 100 / occurrences(t);
    const monthly = (extra * occurrences(t)) / 12;
    if (monthly < 1) continue;
    out.push({
      id: `pension:${t.id}`, kind: 'pension', item: { id: t.id, label: label(t) }, to: { id: t.id, label: label(t), kind: 'investment' }, ...base(t, extra, monthly), from: asOf,
      percent_of_pay: PENSION_PERCENT,
      changes: { update: [{ id: t.id, kind: 'transaction', set: { amount: text(amountOn(t, asOf) + extra) }, from: asOf }] },
    });
  }

  const highest = owingDebts(ctx, asOf, ctx.goal.goal.series === 'debt')[0];
  if (highest && Number(highest.interestRate ?? 0) > 0) {
    const payment = amountOn(highest, asOf);
    const extra = Math.max(25, Math.round((payment * 0.1) / 5) * 5);
    const toDecider = ctx.decider !== null && ctx.decider.id !== highest.id;
    const roll = toDecider ? { then: asDebt(ctx.decider!), redirect: [{ from: highest.id, to: ctx.decider!.id, when: 'ends' as const }] } : rollOn(ctx);
    out.push({
      id: `overpay:${highest.id}`, kind: 'overpay', item: { id: highest.id, label: label(highest) }, to: asDebt(highest), ...(roll.then ? { then: roll.then } : {}),
      ...base(highest, extra, extra), from: asOf,
      changes: steps([{ id: highest.id, kind: 'transaction', set: { amount: text(payment + extra) }, from: asOf }], roll),
    });
  }
  return out;
}

/** Goals a move can bring closer: a target to reach, or a debt to clear. A pot "running dry" is not one. */
const movable = (g: GoalProgress): boolean => g.goal.operation === 'crossover' || (g.goal.operation === 'crossunder' && g.goal.series === 'debt');

/** The day the goal's series meets its target, read straight between the points of the line around it; null when it never does. */
function crossing(f: Forecasted): Day | null {
  const goal = f.goals[0]!;
  const target = core.toDouble(goal.targetValue);
  const meets = (v: number) => (goal.goal.operation === 'crossunder' ? v <= target : v >= target);
  const points = [
    { day: f.today, value: core.toDouble(goal.currentValue) },
    ...f.forecast.cumulative[goal.goal.series].map((b) => ({ day: core.addDays(b.end, -1), value: core.toDouble(b.amount) })),
  ];
  const i = points.findIndex((p) => meets(p.value));
  if (i < 0) return null;
  if (i === 0) return points[0]!.day;
  const a = points[i - 1]!;
  const b = points[i]!;
  return core.addDays(a.day, Math.ceil(((target - a.value) / (b.value - a.value)) * (b.day - a.day)));
}

function daysSooner(before: Forecasted, after: Forecasted, row: { before: string | null; after: string | null }): number | null {
  if (row.before === null || row.after === null) return null;
  if (before.goals[0]!.goal.series === 'debt') return parseDay(row.before) - parseDay(row.after);
  const was = crossing(before);
  const now = crossing(after);
  return was === null || now === null ? null : was - now;
}

/** The plan with only goal `id`: every other goal's date would otherwise be read off a 50-year line on each run. */
const onlyGoal = (wire: WirePlan, id: string): WirePlan => ({ ...wire, goals: (wire.goals ?? []).filter((g) => g.id === id) });

/** Run first on a short line: most goals are met within it, and a later one extends it only as far as it needs. */
const FIRST_YEARS = 10;

/** The goal asked for, else the first by priority still to reach, with its forecast on a short line. */
function chooseGoal(wire: WirePlan, waypoints: readonly WaypointInput[], asOf: string, goalId: string | undefined): { solo: WirePlan; f: Forecasted; goal: GoalProgress } | null {
  const order = (wire.goals ?? []).map((g, i) => ({ g, rank: (g.priority ?? 0) * 1000 + i })).sort((a, b) => a.rank - b.rank).map((x) => x.g);
  for (const g of goalId === undefined ? order : order.filter((x) => x.id === goalId)) {
    const solo = onlyGoal(wire, g.id);
    const f = runForecast({ plan: solo, waypoints, asOf }, FIRST_YEARS);
    const progress = f.goals[0];
    if (progress && !progress.isAchieved && movable(progress)) return { solo, f, goal: progress };
  }
  return null;
}

/** When the goal is measured: its by day's month end, else the month it is due. */
const targetDay = (g: GoalProgress): Day | null => g.deadline?.date ?? g.estimatedDate;

/**
 * A move counts when it brings the goal at least a month (or two weeks) sooner, makes it reachable,
 * or closes at least 1% of the distance still to go (and at least 100 in the plan's money) by the day
 * it is measured.
 */
function meaningful(e: MoveEffect, goal: GoalProgress): boolean {
  if (e.becomes_reachable || (e.months_sooner ?? 0) >= 1 || (e.days_sooner ?? 0) >= 14) return true;
  const distance = Math.abs(core.toDouble(core.sub(goal.targetValue, goal.currentValue)));
  return Number(e.gain) >= Math.max(100, distance / 100) && (e.months_sooner ?? 0) >= 0;
}

/** A gain past the target is not shortfall closed: a goal already on track closes none, and a move overshooting closes only what was short. */
const shortfallClosed = (e: MoveEffect): number => Number(e.shortfall_before ?? 0) - Number(e.shortfall_after ?? 0);

/**
 * Biggest first. With a by day: the shortfall closed by then, then days sooner. Without: made
 * reachable, days sooner, then the gain at the month it was due. Same effect: the cheaper first.
 */
function rank(byDay: boolean) {
  const keys = (m: Move): number[] => {
    const sooner = m.effect.becomes_reachable ? 1e6 : (m.effect.days_sooner ?? 0);
    return [...(byDay ? [shortfallClosed(m.effect), sooner] : [sooner, Number(m.effect.gain)]), -Number(m.monthly)];
  };
  return (a: Move, b: Move): number => {
    const x = keys(a);
    const y = keys(b);
    for (let i = 0; i < x.length; i++) if (x[i] !== y[i]) return y[i]! - x[i]!;
    return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
  };
}

/**
 * The goal's biggest moves on `asOf`: every candidate change set run through the engine on a line
 * long enough to see the goal (a year past its by month and due month, so a move that delays it stays
 * on the line; at most 30 years), ranked by
 * `rank`, one move per item, weak moves dropped. Deterministic for the same map and day.
 */
export function biggestMoves(wire: WirePlan, waypoints: readonly WaypointInput[], asOf: string, options: MoveOptions = {}): BiggestMoves {
  const limit = options.limit ?? 3;
  const today = parseDay(asOf);
  const chosen = chooseGoal(wire, waypoints, asOf, options.goalId);
  const target = chosen === null ? null : targetDay(chosen.goal);
  if (chosen === null || target === null) return { moves: [], measuredOn: null, goalId: chosen?.goal.goal.id ?? null };
  const furthest = Math.max(target, chosen.goal.estimatedDate ?? target) as Day;
  const years = Math.max(1, Math.ceil((calendarMonths(today, furthest) + 13) / 12));
  if (years > MOVES_MAX_YEARS) return { moves: [], measuredOn: null, goalId: chosen.goal.goal.id };
  const { solo } = chosen;
  const before = years === chosen.f.years ? chosen.f : runForecast({ plan: solo, waypoints, asOf }, years);
  const goal = before.goals[0]!;
  const plan = planAsOf(decode(wire), waypoints, asOf);
  const partial = {
    wire, asOf, today, plan, goal, limit, horizon: formatDay(core.addDays(before.forecast.cumulative.netWorth.at(-1)!.end, -1)), transactions: wire.transactions ?? [], decider: null, runnerUp: null,
    debts: new Map(plan.transactions.filter((t) => t.type === 'debt').map((t) => [t.id, t])),
  };
  const ctx: Context = { ...partial, ...debtOrder(partial) };

  let measuredOn: string | null = null;
  const ranked = candidates(ctx).flatMap((c): Move[] => {
    let changed: WirePlan;
    try {
      changed = applyChangeSet(solo, c.changes, { today: asOf }).plan;
    } catch {
      return [];
    }
    const after = runForecast({ plan: changed, waypoints, asOf }, years);
    const row = compareForecasts(before, after).goals[0];
    if (!row || row.gain === null || row.measured_on === null) return [];
    measuredOn = row.measured_on;
    const effect: MoveEffect = {
      goal_id: row.id, before: row.before, after: row.after, months_sooner: row.months_sooner, days_sooner: daysSooner(before, after, row), becomes_reachable: row.becomes_reachable,
      gain: row.gain, measured_on: row.measured_on,
      ...(row.shortfall_before === undefined ? {} : { shortfall_before: row.shortfall_before, shortfall_after: row.shortfall_after }),
    };
    return meaningful(effect, goal) ? [{ ...withoutIdleRoll(c, changed, ctx), effect }] : [];
  });

  ranked.sort(rank(!!goal.deadline));
  const seen = new Set<string>();
  const moves = ranked.filter((m) => !seen.has(m.item.id) && seen.add(m.item.id)).slice(0, limit);
  const date = moves.length > 0 ? dateMove(goal, asOf, measuredOn) : null;
  return { moves: date ? [...moves, date] : moves, measuredOn, goalId: goal.goal.id };
}

/** For a goal behind its by day: the by day it would need. Information only, never ranked, so never first. */
function dateMove(goal: GoalProgress, asOf: string, measuredOn: string | null): Move | null {
  const d = goal.deadline;
  if (!d || d.onTrack || goal.estimatedDate === null) return null;
  const by = formatDay(goal.estimatedDate);
  return {
    id: `date:${goal.goal.id}`, kind: 'date', item: { id: goal.goal.id, label: goal.goal.label }, to: null, amount: '0', frequency: 'monthly', monthly: '0', from: asOf, by,
    changes: { update: [{ id: goal.goal.id, kind: 'goal', set: { targetDate: by } }] },
    effect: {
      goal_id: goal.goal.id, before: by, after: by, months_sooner: 0, days_sooner: 0, becomes_reachable: false, gain: '0',
      measured_on: measuredOn ?? by, shortfall_before: money(d.shortfall), shortfall_after: '0',
    },
  };
}

const PER: Readonly<Record<Region, Readonly<Record<Move['frequency'], string>>>> = {
  uk: { weekly: 'a week', fortnightly: 'a fortnight', monthly: 'a month', quarterly: 'a quarter', yearly: 'a year' },
  us: { weekly: 'a week', fortnightly: 'every two weeks', monthly: 'a month', quarterly: 'a quarter', yearly: 'a year' },
};

/**
 * A move's cost in plain words, as Claude says it: "£21 a month less on Eating out, into Stocks &
 * shares ISA". `format` writes an amount in the plan's currency; `region` picks the plan's words.
 * The web words the same fields in its own language.
 */
export function moveCostText(m: Move, format: (amount: string) => string, region: Region = 'uk'): string {
  const amount = `${format(m.amount)} ${PER[region][m.frequency]}`;
  const then = m.then ? `, then ${m.then.label} once it is paid off` : '';
  switch (m.kind) {
    case 'cut':
      return `${amount} less on ${m.item.label}${m.to && m.to.kind !== 'cash' ? `, into ${m.to.label}` : ', kept as cash'}${then}`;
    case 'redirect':
      return `when ${m.item.label} ends, keep paying its ${amount} into ${m.to!.label} from ${readableMonth(m.from)}${then}`;
    case 'pension':
      return `${m.percent_of_pay}% more of your pay into ${m.item.label}: ${amount}`;
    case 'overpay':
      return `${amount} more on ${m.item.label}${then}`;
    case 'date':
      return `move the date for ${m.item.label} to ${readableMonth(m.by!)}`;
  }
}

/**
 * What a move does for the goal: "2 months sooner (Mar 2029)", "about 3 weeks sooner", "£1,240 closer
 * by Mar 2029", or for a goal behind its by day that the move puts on track, "on track for Jan 2029".
 * A goal already on track for its by day is worded as one without ("£600 more by Mar 2029" at most):
 * it is not getting closer to a target it already meets.
 */
export function moveEffectText(m: Move, format: (amount: string) => string): string {
  const e = m.effect;
  if (m.kind === 'date') return `on track for ${readableMonth(m.by!)}`;
  if (e.becomes_reachable && e.after !== null) return `within reach: ${readableMonth(e.after)}`;
  const behind = e.shortfall_before !== undefined && Number(e.shortfall_before) > 0;
  const onTrack = e.shortfall_before !== undefined && !behind;
  const closer = `${format(e.gain)} ${onTrack ? 'more' : 'closer'} by ${readableMonth(e.measured_on)}`;
  if (behind && Number(e.shortfall_after) === 0 && e.after !== null) return `on track for ${readableMonth(e.after)}`;
  if (behind || e.after === null) return closer;
  if (e.months_sooner) return `${e.months_sooner} month${e.months_sooner === 1 ? '' : 's'} sooner (${readableMonth(e.after)})`;
  const weeks = Math.round((e.days_sooner ?? 0) / 7);
  return weeks >= 2 ? `about ${weeks} weeks sooner` : closer;
}
